import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Select, { SingleValue } from 'react-select'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { getCompanyList, getCompanyAdminList } from '../core/_requests'
import { Company, CompanyAdmin, Vessel } from '../core/_models'
import type { AllotmentAdvanceRecord } from './ViewAllotmentAdvanceModal'

export interface PortageBillModalProps {
  isOpen: boolean
  onClose: () => void
  selectedPeriod?: string
  initialFilters?: {
    companyGroupId?: number | null
    companyAdminId?: number | null
    vesselId?: number | null
    crewId?: number | null
  }
  currentUser?: any
  isSuperAdmin?: boolean
  isCompanyAdmin?: boolean
  isCompanyGroupAdmin?: boolean
  isOperator?: boolean
  isCrewMaster?: boolean
  vessels?: Vessel[]
  allRecords: AllotmentAdvanceRecord[]
  getBalanceCfLastMonth: (record: AllotmentAdvanceRecord) => number | undefined
  getWagesEarned: (record: AllotmentAdvanceRecord) => number
  getTotalClaim: (record: AllotmentAdvanceRecord) => number
  getTotalDeduction: (record: AllotmentAdvanceRecord) => number
  getTotalAllotmentAmount: (record: AllotmentAdvanceRecord) => number
  getBalanceCf: (record: AllotmentAdvanceRecord) => number
}

const formatCurrency = (amount?: number) => {
  if (amount == null || isNaN(amount)) return '0.00'
  return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatMonthLabel = (period?: string) => {
  if (!period) return 'All Periods'
  const [yearText, monthText] = period.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!year || !month) return period
  const date = new Date(year, month - 1, 1)
  return date.toLocaleString('default', { month: 'long', year: 'numeric' })
}

const PortageBillModal: React.FC<PortageBillModalProps> = ({
  isOpen,
  onClose,
  selectedPeriod,
  initialFilters,
  isSuperAdmin,
  isCompanyAdmin,
  isCompanyGroupAdmin,
  isOperator,
  isCrewMaster,
  vessels = [],
  allRecords = [],
  getBalanceCfLastMonth,
  getWagesEarned,
  getTotalClaim,
  getTotalDeduction,
  getTotalAllotmentAmount,
  getBalanceCf,
}) => {
  const [companyGroupId, setCompanyGroupId] = useState<number | null>(null)
  const [companyAdminId, setCompanyAdminId] = useState<number | null>(null)
  const [vesselId, setVesselId] = useState<number | null>(null)
  const [crewId, setCrewId] = useState<number | null>(null)

  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])

  // Reset filters when modal opens
  useEffect(() => {
    if (!isOpen) return
    setCompanyGroupId(initialFilters?.companyGroupId ?? null)
    setCompanyAdminId(initialFilters?.companyAdminId ?? null)
    setVesselId(initialFilters?.vesselId ?? null)
    setCrewId(initialFilters?.crewId ?? null)
  }, [isOpen, initialFilters])

  // Fetch company lists for filters
  useEffect(() => {
    if (!isOpen) return
    if (isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin) {
      getCompanyList()
        .then((data) => setCompanies(data || []))
        .catch(() => setCompanies([]))
    }
    if (isSuperAdmin) {
      getCompanyAdminList()
        .then((data) => setCompanyAdmins(data || []))
        .catch(() => setCompanyAdmins([]))
    }
  }, [isOpen, isSuperAdmin, isCompanyAdmin, isCompanyGroupAdmin])

  // Sub-Company list filtered from companies[] by selected main company
  const filteredSubCompanies = useMemo(() => {
    if (!companyGroupId) return companies
    return companies.filter((ca) => {
      const parentId = (ca as any)?.cga?.id ?? (ca as any)?.companyGroupAdminId
      return parentId === Number(companyGroupId)
    })
  }, [companies, companyGroupId])

  // Filter vessels by selected sub-company first, then main company
  const filteredVessels = useMemo(() => {
    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      v.companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      v.companyAdmin?.cga?.id

    if (companyAdminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === Number(companyAdminId))
    }
    if (companyGroupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === Number(companyGroupId))
    }
    return vessels
  }, [vessels, companyGroupId, companyAdminId])

  // Filtered records matching selected filters
  const filteredRecords = useMemo(() => {
    return allRecords.filter((record) => {
      if (
        companyGroupId &&
        Number((record as any).companyGroupId ?? (record as any).companyGroupAdminId) !== Number(companyGroupId)
      ) {
        return false
      }
      if (
        companyAdminId &&
        Number((record as any).companyAdminId) !== Number(companyAdminId)
      ) {
        return false
      }
      if (
        vesselId &&
        Number(record.vesselId) !== Number(vesselId)
      ) {
        return false
      }
      if (
        crewId != null &&
        Number(record.crewId) !== Number(crewId)
      ) {
        return false
      }
      return true
    })
  }, [allRecords, companyGroupId, companyAdminId, vesselId, crewId])

  // Unique crew list for crew filter dropdown (filtered by selected company / sub-company / vessel)
  const uniqueCrewOptions = useMemo(() => {
    const map = new Map<number, { crewId: number; crewName: string; rankName?: string }>()
    allRecords.forEach((r) => {
      if (
        companyGroupId &&
        Number((r as any).companyGroupId ?? (r as any).companyGroupAdminId) !== Number(companyGroupId)
      ) {
        return
      }
      if (
        companyAdminId &&
        Number((r as any).companyAdminId) !== Number(companyAdminId)
      ) {
        return
      }
      if (
        vesselId &&
        Number(r.vesselId) !== Number(vesselId)
      ) {
        return
      }
      const cId = Number(r.crewId)
      if (cId && !map.has(cId)) {
        map.set(cId, { crewId: cId, crewName: r.crewName, rankName: r.rankName })
      }
    })
    return Array.from(map.values()).sort((a, b) => a.crewName.localeCompare(b.crewName))
  }, [allRecords, companyGroupId, companyAdminId, vesselId])

  const totals = useMemo(() => {
    let totalBalanceCf = 0
    let totalWagesEarned = 0
    let totalClaim = 0
    let totalDeduction = 0
    let totalAllotment = 0
    let totalBalanceWages = 0

    filteredRecords.forEach((record) => {
      totalBalanceCf += getBalanceCfLastMonth(record) ?? 0
      totalWagesEarned += getWagesEarned(record)
      totalClaim += getTotalClaim(record)
      totalDeduction += getTotalDeduction(record)
      totalAllotment += getTotalAllotmentAmount(record)
      totalBalanceWages += getBalanceCf(record)
    })

    return {
      totalBalanceCf,
      totalWagesEarned,
      totalClaim,
      totalDeduction,
      totalAllotment,
      totalBalanceWages,
    }
  }, [
    filteredRecords,
    getBalanceCfLastMonth,
    getWagesEarned,
    getTotalClaim,
    getTotalDeduction,
    getTotalAllotmentAmount,
    getBalanceCf,
  ])

  if (!isOpen) return null

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const marginX = 25

      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(28, 72, 126)
      doc.text('PORTAGE BILL', marginX, 35)

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)

      const selectedVesselName = vesselId
        ? vessels.find((v) => v.id === vesselId)?.fleet_name
        : 'All Vessels'
      const periodText = `Period: ${formatMonthLabel(selectedPeriod)}`
      doc.text(`Vessel: ${selectedVesselName}   |   ${periodText}`, marginX, 50)

      const dateText = `Generated: ${new Date().toLocaleDateString()}`
      doc.text(dateText, pageWidth - marginX - doc.getTextWidth(dateText), 50)

      const headers = [
        [
          'Sr/No',
          'Name',
          'Rank',
          'Vessel',
          'Balance C/F',
          'Wages Earned',
          'Total Claim',
          'Total Deduction',
          'Total Allotment Amt',
          'Balance Wages',
          'Signature',
        ],
      ]

      const body = filteredRecords.map((record, index) => [
        index + 1,
        record.crewName || '-',
        record.rankName || '-',
        record.vesselName || '-',
        `$${formatCurrency(getBalanceCfLastMonth(record))}`,
        `$${formatCurrency(getWagesEarned(record))}`,
        `$${formatCurrency(getTotalClaim(record))}`,
        `$${formatCurrency(getTotalDeduction(record))}`,
        `$${formatCurrency(getTotalAllotmentAmount(record))}`,
        `$${formatCurrency(getBalanceCf(record))}`,
        '',
      ])

      const foot = [
        [
          '',
          'TOTAL',
          '',
          '',
          `$${formatCurrency(totals.totalBalanceCf)}`,
          `$${formatCurrency(totals.totalWagesEarned)}`,
          `$${formatCurrency(totals.totalClaim)}`,
          `$${formatCurrency(totals.totalDeduction)}`,
          `$${formatCurrency(totals.totalAllotment)}`,
          `$${formatCurrency(totals.totalBalanceWages)}`,
          '',
        ],
      ]

      ;(doc as any).autoTable({
        startY: 65,
        head: headers,
        body: body,
        foot: foot,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 5 },
        headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
        footStyles: { fillColor: [240, 243, 246], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 35, halign: 'center' },
          1: { cellWidth: 95 },
          2: { cellWidth: 65 },
          3: { cellWidth: 65 },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'right' },
          8: { halign: 'right' },
          9: { halign: 'right' },
          10: { cellWidth: 85, halign: 'center' },
        },
        didDrawCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 10) {
            const x1 = data.cell.x + 5
            const x2 = data.cell.x + data.cell.width - 5
            const y = data.cell.y + data.cell.height - 8
            doc.setLineWidth(0.5)
            doc.setDrawColor(180, 180, 180)
            doc.line(x1, y, x2, y)
          }
        },
      })

      const finalY = (doc as any).lastAutoTable.finalY + 40
      if (finalY < doc.internal.pageSize.getHeight() - 50) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(50, 50, 50)

        doc.line(marginX, finalY + 20, marginX + 180, finalY + 20)
        doc.text('Master Signature & Stamp', marginX, finalY + 32)

        const rightSigX = pageWidth - marginX - 180
        doc.line(rightSigX, finalY + 20, rightSigX + 180, finalY + 20)
        doc.text('Chief Engineer / Officer Signature', rightSigX, finalY + 32)
      }

      doc.save(`Portage_Bill_${selectedPeriod || 'all'}.pdf`)
      toast.success('Portage Bill PDF exported successfully')
    } catch (error) {
      console.error('Failed to export Portage Bill PDF:', error)
      showErrorToast(error, 'Failed to export Portage Bill PDF', 'load')
    }
  }

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin || isOperator
  const showCrewFilter = isSuperAdmin || isCompanyAdmin || isCompanyGroupAdmin || isOperator || isCrewMaster

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className='modal-dialog modal-dialog-centered modal-xl' style={{ maxWidth: '95%' }}>
        <div className='modal-content shadow-lg'>
          <div className='modal-header bg-light py-3'>
            <div>
              <h4 className='modal-title fw-bolder text-primary mb-1'>Portage Bill Preview</h4>
              <div className='text-muted fs-7'>
                Period: {formatMonthLabel(selectedPeriod)} &nbsp;|&nbsp; Total Matching Crew: {filteredRecords.length}
              </div>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div className='modal-body py-4' style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {/* Filter Bar */}
            {(showCompanyFilter || showSubCompanyFilter || showVesselFilter || showCrewFilter) && (
              <div className='card bg-light border p-3 mb-4'>
                <div className='row g-3 align-items-end'>
                  {showCompanyFilter && (
                    <div className='col-md-3'>
                      <label className='form-label fw-semibold text-dark fs-7'>Company</label>
                      <Select
                        value={
                          companyGroupId && companyAdmins.find((c) => c.id === companyGroupId)
                            ? { value: String(companyGroupId), label: companyAdmins.find((c) => c.id === companyGroupId)!.name }
                            : null
                        }
                        onChange={(opt: SingleValue<{ value: string; label: string }>) => {
                          const val = opt?.value ? Number(opt.value) : null
                          setCompanyGroupId(val)
                          setCompanyAdminId(null)
                          setVesselId(null)
                          setCrewId(null)
                        }}
                        options={[
                          { value: '', label: 'All Companies' },
                          ...companyAdmins.map((c) => ({ value: String(c.id), label: c.name })),
                        ]}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        isSearchable
                        placeholder='All Companies'
                        isClearable
                      />
                    </div>
                  )}

                  {showSubCompanyFilter && (
                    <div className='col-md-3'>
                      <label className='form-label fw-semibold text-dark fs-7'>Sub-Company</label>
                      <Select
                        value={
                          companyAdminId && filteredSubCompanies.find((c) => c.id === companyAdminId)
                            ? { value: String(companyAdminId), label: filteredSubCompanies.find((c) => c.id === companyAdminId)!.name }
                            : null
                        }
                        onChange={(opt: SingleValue<{ value: string; label: string }>) => {
                          const val = opt?.value ? Number(opt.value) : null
                          setCompanyAdminId(val)
                          setVesselId(null)
                          setCrewId(null)
                        }}
                        options={[
                          { value: '', label: 'All Sub-Companies' },
                          ...filteredSubCompanies.map((c) => ({ value: String(c.id), label: c.name })),
                        ]}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        isSearchable
                        placeholder='All Sub-Companies'
                        isClearable
                      />
                    </div>
                  )}

                  {showVesselFilter && (
                    <div className='col-md-3'>
                      <label className='form-label fw-semibold text-dark fs-7'>Vessel</label>
                      <Select
                        value={
                          vesselId && filteredVessels.find((v) => v.id === vesselId)
                            ? { value: String(vesselId), label: filteredVessels.find((v) => v.id === vesselId)!.fleet_name }
                            : null
                        }
                        onChange={(opt: SingleValue<{ value: string; label: string }>) => {
                          const val = opt?.value ? Number(opt.value) : null
                          setVesselId(val)
                          setCrewId(null)
                        }}
                        options={[
                          { value: '', label: 'All Vessels' },
                          ...filteredVessels.map((v) => ({ value: String(v.id), label: v.fleet_name })),
                        ]}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        isSearchable
                        placeholder='All Vessels'
                        isClearable
                      />
                    </div>
                  )}

                  {showCrewFilter && (
                    <div className='col-md-3'>
                      <label className='form-label fw-semibold text-dark fs-7'>Crew</label>
                      <Select
                        value={
                          crewId != null && uniqueCrewOptions.find((c) => Number(c.crewId) === Number(crewId))
                            ? { value: String(crewId), label: `${uniqueCrewOptions.find((c) => Number(c.crewId) === Number(crewId))!.crewName}` }
                            : null
                        }
                        onChange={(opt: SingleValue<{ value: string; label: string }>) => {
                          const val = opt && opt.value !== '' ? Number(opt.value) : null
                          setCrewId(val)
                        }}
                        options={[
                          { value: '', label: 'All Crew' },
                          ...uniqueCrewOptions.map((c) => ({
                            value: String(c.crewId),
                            label: `${c.crewName} (${c.rankName || 'No Rank'})`,
                          })),
                        ]}
                        className='react-select-container'
                        classNamePrefix='react-select'
                        isSearchable
                        placeholder='All Crew'
                        isClearable
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Table */}
            <div className='table-responsive border rounded'>
              <table className='table table-bordered table-striped align-middle mb-0'>
                <thead className='table-dark text-nowrap'>
                  <tr>
                    <th className='text-center' style={{ width: '50px' }}>Sr/No</th>
                    <th>Name</th>
                    <th>Rank</th>
                    <th>Vessel</th>
                    <th className='text-end'>Balance C/F</th>
                    <th className='text-end'>Wages Earned</th>
                    <th className='text-end'>Total Claim</th>
                    <th className='text-end'>Total Deduction</th>
                    <th className='text-end'>Total Allotment Amt</th>
                    <th className='text-end'>Balance Wages</th>
                    <th className='text-center' style={{ minWidth: '130px' }}>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan={11} className='text-center text-muted py-4'>
                        No crew records found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((record, index) => (
                      <tr key={record.crewId || index}>
                        <td className='text-center fw-bold'>{index + 1}</td>
                        <td className='fw-bold text-dark'>{record.crewName}</td>
                        <td>{record.rankName || '-'}</td>
                        <td>{record.vesselName || '-'}</td>
                        <td className='text-end'>${formatCurrency(getBalanceCfLastMonth(record))}</td>
                        <td className='text-end'>${formatCurrency(getWagesEarned(record))}</td>
                        <td className='text-end'>${formatCurrency(getTotalClaim(record))}</td>
                        <td className='text-end'>${formatCurrency(getTotalDeduction(record))}</td>
                        <td className='text-end'>${formatCurrency(getTotalAllotmentAmount(record))}</td>
                        <td className='text-end fw-bolder text-primary'>
                          ${formatCurrency(getBalanceCf(record))}
                        </td>
                        <td className='text-center align-bottom'>
                          <div
                            className='border-bottom border-secondary border-dashed mx-auto mb-1'
                            style={{ width: '80%', height: '24px' }}
                          ></div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {filteredRecords.length > 0 && (
                  <tfoot className='table-light text-nowrap fw-bolder'>
                    <tr>
                      <td colSpan={4} className='text-end'>TOTAL:</td>
                      <td className='text-end'>${formatCurrency(totals.totalBalanceCf)}</td>
                      <td className='text-end'>${formatCurrency(totals.totalWagesEarned)}</td>
                      <td className='text-end'>${formatCurrency(totals.totalClaim)}</td>
                      <td className='text-end'>${formatCurrency(totals.totalDeduction)}</td>
                      <td className='text-end'>${formatCurrency(totals.totalAllotment)}</td>
                      <td className='text-end text-primary'>${formatCurrency(totals.totalBalanceWages)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className='d-flex justify-content-between mt-5 pt-4 px-4 border-top'>
              <div className='text-center' style={{ width: '220px' }}>
                <div className='border-bottom border-dark mb-2' style={{ height: '40px' }}></div>
                <span className='fw-bold text-gray-700 fs-7'>Master Signature & Stamp</span>
              </div>
              <div className='text-center' style={{ width: '250px' }}>
                <div className='border-bottom border-dark mb-2' style={{ height: '40px' }}></div>
                <span className='fw-bold text-gray-700 fs-7'>Chief Engineer / Officer Signature</span>
              </div>
            </div>
          </div>

          <div className='modal-footer bg-light py-2'>
            <button type='button' className='btn btn-light' onClick={onClose}>
              Close
            </button>
            <button type='button' className='btn btn-primary' onClick={handleExportPDF}>
              <i className='bi bi-file-earmark-pdf me-1'></i>
              Export PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PortageBillModal
